import { ReactNode } from 'react';
import {
  AiOutlineCheck,
  AiOutlineClose,
  AiOutlineEdit,
  AiOutlineHourglass,
  AiOutlineSecurityScan,
  AiOutlineSend,
  AiOutlineSync,
} from 'react-icons/ai/index.js';
import {
  Accordion,
  AccordionButton,
  AccordionButtonProps,
  AccordionItem,
  AccordionItemProps,
  AccordionPanel,
  AccordionPanelProps,
  AccordionProps,
  Box,
  BoxProps,
  Icon,
  IconProps,
  useMultiStyleConfig,
} from '@chakra-ui/react';

export const TimeLineStages: Record<string, string[]> = {
  completed: ['completed', 'success'],
  progress: ['progress'],
  failed: ['failed', 'cancelled', 'error'],
  waiting: ['waiting', 'paused', 'unknown', 'not found'],
  validating: ['validating'],
  submitted: ['submitted'],
  drafted: ['drafted'],
};
export const StyleConfigMap: Record<string, TimelineConfig> = {
  completed: {
    icon: AiOutlineCheck,
    color: 'green.700',
    bg: 'green.200',
    hoverBg: 'green.300',
  },
  submitted: {
    icon: AiOutlineSend,
    color: 'orange.700',
    bg: 'orange.200',
    hoverBg: 'orange.300',
  },
  failed: {
    icon: AiOutlineClose,
    color: 'red.700',
    bg: 'red.200',
    hoverBg: 'red.300',
  },
  waiting: {
    icon: AiOutlineHourglass,
    color: 'gray.700',
    bg: 'gray.100',
    hoverBg: 'gray.200',
  },
  validating: {
    icon: AiOutlineSecurityScan,
    color: 'yellow.700',
    bg: 'yellow.200',
    hoverBg: 'yellow.300',
  },
  progress: {
    icon: AiOutlineSync,
    color: 'blue.700',
    bg: 'blue.200',
    hoverBg: 'blue.300',
  },
  drafted: {
    icon: AiOutlineEdit,
    color: 'yellow.700',
    bg: 'yellow.200',
    hoverBg: 'yellow.300',
  },
};

export const getConfigStyles = (status: string) => {
  const configStatus = Object.keys(TimeLineStages).filter(
    (value) =>
      TimeLineStages[value].filter((a) => status.toLowerCase().includes(a))[0],
  )[0];
  return StyleConfigMap[configStatus]
    ? StyleConfigMap[configStatus]
    : StyleConfigMap.completed;
};
export interface TimelineDataProps {
  title: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  titleIcon?: any;
  status: string;
  details: ReactNode;
  subTitle?: ReactNode;
}
export interface TimelineConfig {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  color: string;
  bg: string;
  hoverBg: string;
}

export interface AccordionTimelineProps {
  data: TimelineDataProps[];
  containerProps?: BoxProps;
  accordionProps?: AccordionProps;
  itemProps?: AccordionItemProps;
  buttonProps?: AccordionButtonProps;
  titleIconProps?: IconProps;
  iconContainerProps?: BoxProps;
  iconProps?: IconProps;
  subTitleProps?: BoxProps;
  detailsProps?: AccordionPanelProps;
}

export const AccordionTimeline = (props: AccordionTimelineProps) => {
  const {
    data,
    containerProps,
    accordionProps,
    itemProps,
    buttonProps,
    titleIconProps,
    iconContainerProps,
    iconProps,
    subTitleProps,
    detailsProps,
  } = props;
  const styles = useMultiStyleConfig(`AccordionTimeline`, {});

  return (
    <Box {...containerProps}>
      <Accordion sx={{ ...styles.Accordion }} allowMultiple {...accordionProps}>
        {data.map((row, index) => {
          const config = getConfigStyles(row.status);
          return (
            <AccordionItem
              sx={{ ...styles.AccordionItem }}
              key={index}
              {...itemProps}
            >
              <h2>
                <AccordionButton
                  sx={{ ...styles.AccordionButton }}
                  _hover={{ background: config.hoverBg }}
                  bg={config.bg}
                  color={config.color}
                  {...buttonProps}
                >
                  {row.titleIcon && (
                    <Icon
                      as={row.titleIcon}
                      bg={config.color}
                      sx={{ ...styles.TitleIcon }}
                      {...titleIconProps}
                    />
                  )}
                  {row.title}
                  <Box
                    sx={{ ...styles.Box }}
                    border={`1px solid ${config.bg}`}
                    {...iconContainerProps}
                  >
                    <Icon
                      as={config.icon}
                      sx={{ ...styles.Icon }}
                      {...iconProps}
                    />
                  </Box>
                  {row.subTitle && (
                    <Box
                      sx={{ ...styles.SubTitle }}
                      bg={config.color}
                      {...subTitleProps}
                    >
                      {row.subTitle}
                    </Box>
                  )}
                </AccordionButton>
              </h2>
              <AccordionPanel
                sx={{ ...styles.AccordionPanel, ...detailsProps }}
              >
                {row.details}
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </Box>
  );
};
